import { ChevronDown, Search } from "lucide-react";
import { useState } from "react";

import { Checkbox } from "../../../components/ui/checkbox";
import { Input } from "../../../components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "../../../components/ui/popover";
import { cn } from "../../../handlers/cn.utils";
import {
	CLAIMABLE_COMPLETION_PCT,
	type CompletionBand,
	countActiveFilters,
	NO_REWARD_FILTERS,
	type RewardActivityType,
	type RewardFilters,
	type RewardStatus,
	STATUS_LABELS,
} from "../../../handlers/rewards";

const STATUS_OPTIONS: Array<{ value: RewardStatus; label: string }> = [
	{ value: "PENDING", label: STATUS_LABELS.PENDING },
	{ value: "CLAIMED", label: STATUS_LABELS.CLAIMED },
	{ value: "REDEEMED", label: STATUS_LABELS.REDEEMED },
];

const BAND_OPTIONS: Array<{ value: CompletionBand; label: string }> = [
	{ value: "none", label: "Not started" },
	{ value: "partial", label: `Under ${CLAIMABLE_COMPLETION_PCT}%` },
	{ value: "ready", label: `${CLAIMABLE_COMPLETION_PCT}% and over` },
];

/**
 * Job leads are a fourth kind of reward the server can send, left off until the page has one to
 * show. A ticked box still excludes them, which is the same answer the missing option gives.
 */
const TYPE_OPTIONS: Array<{ value: RewardActivityType; label: string }> = [
	{ value: "REFERRAL", label: "Referral" },
	{ value: "RESUME_COMPLETION", label: "Resume completion" },
];

interface ActivityFiltersProps {
	filters: RewardFilters;
	onChange: (filters: RewardFilters) => void;
}

function toggle<T>(values: T[], value: T): T[] {
	return values.includes(value) ? values.filter((entry) => entry !== value) : [...values, value];
}

/**
 * One menu of checkboxes.
 *
 * Nothing ticked means every value passes, so the trigger reads as the plain column name until the
 * reader narrows it — a menu that opened on "all three ticked" would say the same thing while
 * looking like a filter is running.
 *
 * A pick closes the menu so the table underneath is visible again straight away. Stacking a second
 * value costs a reopen, which the count on the trigger makes obvious enough to find.
 */
function FilterMenu<T extends string>({
	label,
	options,
	selected,
	onToggle,
}: {
	label: string;
	options: Array<{ value: T; label: string }>;
	selected: T[];
	onToggle: (value: T) => void;
}) {
	const [open, setOpen] = useState(false);

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<button
					type="button"
					className={cn(
						"inline-flex h-9 items-center gap-2 rounded-md border px-3 text-sm font-medium transition-colors font-inter",
						selected.length > 0
							? "border-primary-700 text-primary-700"
							: "border-gray-200 text-gray-600 hover:bg-gray-50",
					)}
				>
					{label}
					{selected.length > 0 && (
						<span className="rounded-full bg-primary-700 px-1.5 text-xs font-semibold text-white tabular-nums">
							{selected.length}
						</span>
					)}
					<ChevronDown className="size-4 text-gray-400" />
				</button>
			</PopoverTrigger>

			<PopoverContent align="end" className="w-56 p-1.5">
				{options.map((option) => (
					<label
						key={option.value}
						className="flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-50 font-inter"
					>
						<Checkbox
							checked={selected.includes(option.value)}
							onCheckedChange={() => {
								onToggle(option.value);
								setOpen(false);
							}}
						/>
						{option.label}
					</label>
				))}
			</PopoverContent>
		</Popover>
	);
}

function ActivityFilters({ filters, onChange }: ActivityFiltersProps) {
	const active = countActiveFilters(filters);

	return (
		<div className="flex flex-wrap items-center gap-2">
			<div className="relative min-w-[200px] flex-1 sm:max-w-xs">
				<Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400" />
				<Input
					type="search"
					value={filters.search}
					onChange={(event) => onChange({ ...filters, search: event.target.value })}
					placeholder="Search by name or email"
					aria-label="Search rewards"
					className="border-gray-200 pl-9 font-inter"
				/>
			</div>

			<div className="flex flex-wrap items-center gap-2 sm:ml-auto">
				<FilterMenu
					label="Status"
					options={STATUS_OPTIONS}
					selected={filters.statuses}
					onToggle={(value) => onChange({ ...filters, statuses: toggle(filters.statuses, value) })}
				/>
				<FilterMenu
					label="Completion"
					options={BAND_OPTIONS}
					selected={filters.bands}
					onToggle={(value) => onChange({ ...filters, bands: toggle(filters.bands, value) })}
				/>
				<FilterMenu
					label="Activity"
					options={TYPE_OPTIONS}
					selected={filters.types}
					onToggle={(value) => onChange({ ...filters, types: toggle(filters.types, value) })}
				/>
				{active > 0 && (
					<button
						type="button"
						onClick={() => onChange(NO_REWARD_FILTERS)}
						className="h-9 rounded-md px-2 text-sm font-medium text-gray-500 transition-colors hover:text-gray-900 font-inter"
					>
						Clear
					</button>
				)}
			</div>
		</div>
	);
}

export default ActivityFilters;
